/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import { AelEvents, BuilderPermission, EmailTemplateType, getAelEventsArray } from '@gh/builder-shared-data';
import { AppRouter, getRouteInitData, getRouteStaticData, HelpID } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { RouteDataService, SlicedDataSource } from '@gh/core-data';
import { cursorTracker, GridDefinitionFactory, newGridPaginationCursor } from '@gh/core-ui-grid';
import { EmailTemplateTabBusinessService } from '../../resources/email-template-tab-business.service';
import { EmailTemplateList } from '../../resources/email-template.model';

@Component({
  selector: 'gh-email-template-list-tab',
  template: require('./email-template-list-tab.component.html'),
})
export class EmailTemplateListTabComponent implements OnInit {
  dataSource: SlicedDataSource<EmailTemplateList>;
  gridDefinition: any;
  templateType: EmailTemplateType;
  cursor = newGridPaginationCursor();

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private routeData: RouteDataService,
              private businessService: EmailTemplateTabBusinessService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private navigationService: BuilderNavigationService,
              private route: ActivatedRoute) {
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({}, {
      getRowNodeId: (template: EmailTemplateList) => template.id,
    });
  }

  get helpId(): HelpID {
    return {
      [EmailTemplateType.System]: 'setup/email-templates/system/list',
      [EmailTemplateType.Job]: 'setup/email-templates/templates-job',
      [EmailTemplateType.Internal]: 'setup/email-templates/internal/list',
      [EmailTemplateType.Layout]: 'setup/email-templates/layout/list',
    }[this.templateType];
  }

  get title(): string {
    return {
      [EmailTemplateType.System]: 'lbl_system_templates',
      [EmailTemplateType.Internal]: 'lbl_internal_templates',
      [EmailTemplateType.Job]: 'lbl_custom_email_templates',
      [EmailTemplateType.Layout]: 'lbl_layout_templates',
    }[this.templateType];
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.templateType = getRouteStaticData(this.route)['type'];
    this.gridDefinition.bind(gridLayout);

    const tracker = this.routeData.tracker().withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.loadData());
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource({ type: this.templateType });
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/email-templates/templates/new'], { queryParams: { type: this.templateType } });
  }

  onEdit(id: ID): void {
    if (this.canBeEdited()) {
      this.router.navigateToChild([`/setup/email-templates/templates/${id}`]);
    }
  }

  onDelete(template: EmailTemplateList): void {
    const flow = this.pipelines.toggleItem({
      question: { id: 'msg_template_delete', params: { name: template.name } },
      validate: this.commonActions.validate((state: CommonFlowState<EmailTemplateList>) =>
        this.businessService.checkLevel(state.value)),
      execute: this.commonActions.execute((state: CommonFlowState<EmailTemplateList>) =>
        this.businessService.remove(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: template,
    });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId || void 0,
      entityType: entityType ? entityType : 'EMAIL_TEMPLATE',
      eventTypeIds: getAelEventsArray(AelEvents.EmailTemplates[this.templateType]),
      label: this.title,
    });
  }

  isJob(): boolean {
    return this.templateType === EmailTemplateType.Job;
  }

  canBeEdited(): boolean {

    const type = this.templateType;

    if (this.userContext.officeView) {
      if (type === EmailTemplateType.System) {
        return (this.userContext.hasPermission(BuilderPermission.Setup.Templates.System.EDIT)
          || this.userContext.hasPermission(BuilderPermission.Setup.Templates.System.VIEW));
      }
      if (type === EmailTemplateType.Job) {
        return (this.userContext.hasPermission(BuilderPermission.Setup.Templates.Job.EDIT)
          || this.userContext.hasPermission(BuilderPermission.Setup.Templates.Job.VIEW));
      }
      return false;
    }
    if (this.userContext.masterView) {
      return !(type === EmailTemplateType.Internal);
    }
    return this.userContext.corporateView;
  }
}
